import React, { useState } from 'react';
import {
  Box, Breadcrumbs, Button, Chip, CircularProgress, Collapse, Container,
  Dialog, DialogContent, Divider, Drawer, IconButton, Link as MuiLink,
  Slide, Snackbar, Tooltip, Typography,
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import NavigateNextRoundedIcon from '@mui/icons-material/NavigateNextRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DriveFileRenameOutlineRoundedIcon from '@mui/icons-material/DriveFileRenameOutlineRounded';
import LibraryBooksRoundedIcon from '@mui/icons-material/LibraryBooksRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';

import ScreenerTablePro from './ScreenerTablePro';
import QueryBuilder from './QueryBuilder';
import ScreenPresets from './ScreenPresets';
import SaveScreener from './SaveScreener';
import RenameScreener from './RenameScreener';
import ManageColumnsDialog from './ManageTableColumns.js';
import DownloadTableData from './DownloadTableData/index.js';
import PremiumBanner from '../Pricing/PremiumBanner';
import QuickFilterBar from './QuickFilterBar';
import { options, DEFAULT_TBL_COLS, mainHeadCols, userHasPresets } from '../../hook/useScreenerState';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function IndexScreenerLayout({ state, pageProps = {}, breadcrumbs = [], pageTitle, pageDesc }) {
  const {
    user, isPremium,
    screenerMetrics, estimatedResults, showingResults, inputText,
    userPresets, userUpdatedPreset, userDeletedPreset,
    activePresetName, activePresetDesc, activePresetCode,
    presetCriteria,
    tableCols, columnGroups, offset,
    isLoading, isTableLoading, saveLoading, message, banner,
    renameDialog,
    setFormula, setTableCols, setPresetCriteria,
    setActivePresetName, setActivePresetDesc, setActivePresetCode,
    setBanner, setOffset, setUserUpdatedPreset, setUserDeletedPreset,
    setColumnGroups,
    handleNewMetrics, updatePreset, renamePreset, deletePreset,
    handleDownload, onSortEnd, runQuery,
    handleRenameOpen, handleRenameClose, handleBannerClose, handleExited,
    logoutUser, handleNewScreen, tryThisQuery,
  } = state;

  const { pageLoading } = pageProps;
  const [mobilePresetsOpen, setMobilePresetsOpen] = useState(false);
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  const hasResults = Boolean(estimatedResults);
  const hasQuery = Boolean(inputText?.trim());

  // Reusable preset props for sidebar + mobile drawer
  const presetProps = {
    setFormula, setBanner, deletePreset, renamePreset, setTableCols, setPresetCriteria,
    setActivePresetName, setActivePresetDesc, setActivePresetCode,
    presetCriteria, userPresets, activePresetName,
    userDeletedPreset, userUpdatedPreset,
    defaultScreenerTableCols: DEFAULT_TBL_COLS, isPremium,
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fafbfc' }}>
      <Container maxWidth="xl" sx={{ pb: 6 }}>

        {/* ── Breadcrumb ── */}
        <Box component="nav" sx={{ pt: { xs: 1.5, sm: 2 }, pb: 0.5 }}>
          <Breadcrumbs
            separator={<NavigateNextRoundedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />}
            aria-label="breadcrumb"
          >
            <MuiLink underline="hover" href="/"
              sx={{ color: 'text.secondary', fontSize: '0.78rem', lineHeight: 1, '&:hover': { color: 'primary.main' } }}>
              Home
            </MuiLink>
            {breadcrumbs.map((crumb, i) => {
              const isLast = i === breadcrumbs.length - 1;
              return isLast ? (
                <Typography key={crumb.label} sx={{ color: 'text.primary', fontSize: '0.78rem', fontWeight: 500, lineHeight: 1 }}>
                  {crumb.label}
                </Typography>
              ) : (
                <MuiLink key={crumb.label} underline="hover" href={crumb.href}
                  sx={{ color: 'text.secondary', fontSize: '0.78rem', lineHeight: 1, '&:hover': { color: 'primary.main' } }}>
                  {crumb.label}
                </MuiLink>
              );
            })}
          </Breadcrumbs>
        </Box>

        {/* ── Two-column body ── */}
        <Box sx={{ display: 'flex', gap: { md: 0 }, alignItems: 'flex-start', mt: { xs: 0.5, md: 1 } }}>

          {/* ▸▸ SIDEBAR (desktop only) ▸▸ */}
          <Box
            component="aside"
            sx={{
              display: { xs: 'none', md: 'flex' },
              flexDirection: 'column',
              width: { md: 256, lg: 272 },
              flexShrink: 0,
              position: 'sticky',
              top: 72,
              maxHeight: 'calc(100vh - 88px)',
              overflowY: 'auto',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
              pr: { md: 3, lg: 4 },
              borderRight: '1px solid',
              borderColor: 'rgba(0,0,0,0.06)',
              mr: { md: 3, lg: 4 },
              pt: 1,
            }}
          >
            {/* Sidebar header */}
            <Typography
              variant="h1"
              sx={{
                fontSize: '1.25rem',
                fontWeight: 700,
                color: 'text.primary',
                letterSpacing: '-0.02em',
                lineHeight: 1.3,
              }}
            >
              {pageTitle}
            </Typography>
            {pageDesc && (
              <Typography variant="body2"
                sx={{ mt: 0.75, fontSize: '0.8rem', color: 'text.secondary', lineHeight: 1.55, opacity: 0.85 }}>
                {pageDesc}
              </Typography>
            )}

            {/* New Screen action */}
            <Button
              fullWidth
              size="small"
              variant="outlined"
              startIcon={<AddRoundedIcon />}
              data-event-name="new-screen"
              data-event-category="stock-screener"
              data-event-label="new-query"
              onClick={handleNewScreen}
              sx={{
                textTransform: 'none', fontSize: '0.82rem', fontWeight: 500,
                borderRadius: '10px', mt: 2, mb: 0.5, justifyContent: 'flex-start',
                borderColor: 'rgba(0,0,0,0.12)',
                color: 'text.secondary',
                '&:hover': { borderColor: 'primary.main', color: 'primary.main', bgcolor: 'rgba(25,118,210,0.04)' },
              }}
            >
              New Screen
            </Button>

            <Divider sx={{ my: 1.5, borderColor: 'rgba(0,0,0,0.06)' }} />

            <ScreenPresets {...presetProps} sidebarMode />
          </Box>

          {/* ▸▸ MAIN CONTENT COLUMN ▸▸ */}
          <Box sx={{ flex: 1, minWidth: 0, maxWidth: { md: 'calc(100% - 288px)', lg: 'calc(100% - 320px)' } }}>

            {/* ── Mobile header ── */}
            <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 2, mt: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                <Typography
                  variant="h1"
                  sx={{ fontSize: '1.35rem', fontWeight: 700, color: 'text.primary', letterSpacing: '-0.02em', lineHeight: 1.3 }}
                >
                  {pageTitle}
                </Typography>
                <Button
                  size="small"
                  variant="text"
                  startIcon={<LibraryBooksRoundedIcon sx={{ fontSize: '1rem !important' }} />}
                  onClick={() => setMobilePresetsOpen(true)}
                  sx={{
                    textTransform: 'none', fontSize: '0.82rem', fontWeight: 500,
                    borderRadius: '10px', flexShrink: 0, color: 'text.secondary',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
                  }}
                >
                  Screens
                </Button>
              </Box>
              {pageDesc && (
                <Typography variant="body2"
                  sx={{ mt: 0.25, fontSize: '0.8rem', color: 'text.secondary', lineHeight: 1.55, display: { xs: 'none', sm: 'block' } }}>
                  {pageDesc}
                </Typography>
              )}
            </Box>

            <Box
              id="build-query"
              sx={{
                bgcolor: '#fff',
                borderRadius: { xs: '16px', md: '20px' },
                border: '1px solid',
                borderColor: hasQuery ? 'rgba(25,118,210,0.18)' : 'rgba(0,0,0,0.08)',
                boxShadow: hasQuery
                  ? '0 0 0 1px rgba(25,118,210,0.06), 0 2px 8px rgba(25,118,210,0.06)'
                  : '0 1px 3px rgba(0,0,0,0.04)',
                p: { xs: 2, sm: 2.5, md: 3 },
                mb: 3,
                transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                scrollMarginTop: 'calc(56px + 26px)',
              }}
            >
              {/* Card header */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                  {activePresetName ? (
                    <>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600, fontSize: '0.95rem', lineHeight: 1.3,
                          textTransform: 'capitalize',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}
                      >
                        {activePresetName}
                      </Typography>
                      {user?.accessToken && (
                        <Tooltip title="Rename" placement="right" disableInteractive arrow>
                          <IconButton
                            onClick={() => handleRenameOpen()} size="small"
                            disabled={userPresets?.find(item => item?.code === activePresetCode)?.isSystemDefault || false}
                            sx={{ p: 0.5 }}
                          >
                            <DriveFileRenameOutlineRoundedIcon sx={{ fontSize: '0.95rem', color: 'text.disabled' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <AutoAwesomeRoundedIcon sx={{ fontSize: '1.05rem', color: 'primary.main', opacity: 0.7 }} />
                      <Typography variant="subtitle1"
                        sx={{ fontWeight: 500, fontSize: '0.92rem', color: 'text.secondary', lineHeight: 1.3 }}>
                        Describe your stock criteria
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Matching indicator — compact pill, desktop only */}
                {hasQuery && (
                  <Chip
                    size="small"
                    label={
                      isLoading
                        ? <CircularProgress size={12} thickness={5} sx={{ color: 'primary.main' }} />
                        : `${estimatedResults ?? 0} match${estimatedResults === 1 ? '' : 'es'}`
                    }
                    sx={{
                      height: 26, fontSize: '0.75rem', fontWeight: 600,
                      bgcolor: hasResults ? 'rgba(25,118,210,0.08)' : 'rgba(0,0,0,0.04)',
                      color: hasResults ? 'primary.main' : 'text.secondary',
                      border: 'none', display: { xs: 'none', sm: 'flex' },
                      '& .MuiChip-label': { px: 1.5 },
                    }}
                  />
                )}
              </Box>

              {/* Preset description */}
              {activePresetDesc && (
                <Typography variant="body2" sx={{ mb: 1.5, fontSize: '0.82rem', color: 'text.secondary', lineHeight: 1.5 }}>
                  {activePresetDesc}
                </Typography>
              )}

              {/* Query Builder */}
              <QueryBuilder
                options={options}
                value={inputText}
                setFormula={setFormula}
                errorMessage={screenerMetrics?.err?.message}
                estimatedResults={estimatedResults}
                isLoading={isTableLoading}
                handleNewMetrics={handleNewMetrics}
                saveScreener={
                  <SaveScreener
                    updatePreset={updatePreset}
                    isPremium={isPremium}
                    saveLoading={saveLoading}
                    setBanner={setBanner}
                    userUpdatedPreset={userUpdatedPreset}
                    activePresetName={activePresetName}
                    activePresetDesc={activePresetDesc}
                    userHasPresets={userHasPresets(userPresets)}
                    user={user}
                  />
                }
              />

              {/* Mobile-only matching counter */}
              {hasQuery && (
                <Box sx={{ display: { xs: 'flex', sm: 'none' }, alignItems: 'center', gap: 1, mt: 1.5, pt: 1, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                    Matching:
                  </Typography>
                  {isLoading
                    ? <CircularProgress size={14} thickness={5} sx={{ color: 'primary.main' }} />
                    : <Typography variant="caption" sx={{ fontWeight: 700, color: hasResults ? 'primary.main' : 'text.secondary' }}>
                      {estimatedResults ?? 0} stock{estimatedResults === 1 ? '' : 's'}
                    </Typography>
                  }
                </Box>
              )}
            </Box>

            {/* ── Quick Filters (progressive disclosure) ── */}
            {/* <Box sx={{ mb: 2.5 }}>
              <Button
                size="small"
                variant="text"
                startIcon={<TuneRoundedIcon sx={{ fontSize: '1rem !important' }} />}
                onClick={() => setFiltersExpanded(prev => !prev)}
                sx={{
                  textTransform: 'none', fontSize: '0.82rem', fontWeight: 500,
                  color: 'text.secondary', borderRadius: '10px', mb: 0.5,
                  bgcolor: filtersExpanded ? 'rgba(0,0,0,0.09)' : 'inherit',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
                }}
              >
                {filtersExpanded ? 'Hide quick filters' : 'Quick filters'}
              </Button>
              <Collapse in={filtersExpanded} timeout={250}>
                <Box sx={{ pt: 0.5 }}>
                  <QuickFilterBar inputText={inputText} runQuery={runQuery} options={options} />
                </Box>
              </Collapse>
            </Box> */}

            <Collapse
              style={{ transformOrigin: '0 0 0' }}
              {...(hasResults ? { timeout: 800 } : {})}
              in={hasResults}
            >
              {/* Results header bar */}
              <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                mb: 1.5, px: 0.5,
              }}>
                {/* <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '1.15rem', color: 'text.primary', lineHeight: 1 }}>
                    {isLoading
                      ? <CircularProgress size={18} thickness={5} sx={{ color: 'primary.main' }} />
                      : (estimatedResults ?? 0)}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.82rem', color: 'text.secondary', fontWeight: 500 }}>
                    matching stock{estimatedResults === 1 ? '' : 's'}
                  </Typography>
                </Box> */}
                {activePresetName && (
                  <Chip
                    size="small"
                    label={activePresetName}
                    sx={{
                      height: 24, fontSize: '0.72rem', fontWeight: 600,
                      textTransform: 'capitalize', maxWidth: 180,
                      bgcolor: 'rgba(0,0,0,0.04)', color: 'text.secondary', border: 'none',
                    }}
                  />
                )}
              </Box>

              {/* Table container */}
              <Box sx={{
                borderRadius: '14px', border: '1px solid rgba(0,0,0,0.07)',
                bgcolor: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.03)', overflow: 'hidden',
              }}>
                <Box sx={{ pt: 2, px: 2, pb: 0 }}>
                  <QuickFilterBar inputText={inputText} runQuery={runQuery} options={options} tableCols={tableCols} setTableCols={setTableCols} />
                </Box>
                <ScreenerTablePro
                  rows={screenerMetrics ? screenerMetrics : []}
                  headCells={tableCols}
                  setTableCols={setTableCols}
                  defaultHeadCells={tableCols}
                  wrapperClass={'screener-table'}
                  shortWrapperClass={'screener-field'}
                  quickSearch={true}
                  needToolbar={true}
                  fullToolbarAccess={false}
                  createSections={false}
                  noOfRows={25}
                  estimatedResults={estimatedResults ?? 0}
                  isLoading={isTableLoading}
                  setOffset={setOffset}
                  offset={offset}
                  mounted={false}
                  isPremium={isPremium}
                  setBanner={setBanner}
                  manageColumns={
                    <ManageColumnsDialog
                      arrayOrder={mainHeadCols}
                      columns={tableCols}
                      setColumnGroups={setColumnGroups}
                      setColumns={setTableCols}
                      onSortEnd={onSortEnd}
                    />
                  }
                  download={
                    <DownloadTableData
                      handleDownload={handleDownload}
                      user={user?.accessToken}
                      message={message}
                      logout={logoutUser}
                      isPremium={isPremium}
                    />
                  }
                  tableName={
                    <Box sx={{ mr: 'auto', display: 'flex', alignItems: 'center', '&:hover .rename-icon': { color: 'primary.main' } }}>
                      <Typography sx={{ fontSize: '1rem', fontWeight: 600, ml: 2, mr: 0.5, textTransform: 'capitalize' }}>
                        {activePresetName}
                      </Typography>
                      {user?.accessToken && (
                        <IconButton size="small" onClick={() => handleRenameOpen()}>
                          <DriveFileRenameOutlineRoundedIcon className="rename-icon" sx={{ color: 'text.disabled', fontSize: '0.95rem' }} />
                        </IconButton>
                      )}
                    </Box>
                  }
                  results={showingResults
                    ? <Typography variant="body2" sx={{ fontSize: '.8rem', fontWeight: 500, ml: 2, mr: 'auto', color: 'text.secondary', display: { xs: 'none', sm: 'block' } }}>
                      {estimatedResults} result{estimatedResults <= 1 ? '' : 's'}
                    </Typography>
                    : ''}
                  activePresetName={activePresetName ?? ''}
                />
              </Box>
            </Collapse>
          </Box>
        </Box>
      </Container>

      {/* ── Mobile presets drawer ── */}
      <Drawer
        anchor="bottom"
        open={mobilePresetsOpen}
        onClose={() => setMobilePresetsOpen(false)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            maxHeight: '85vh',
            px: 2.5, pt: 1.5, pb: 3,
          },
        }}
      >
        {/* Drag handle */}
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 0.5, pb: 1 }}>
          <Box sx={{ width: 36, height: 4, borderRadius: 2, bgcolor: 'rgba(0,0,0,0.15)' }} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '1.05rem' }}>Screens</Typography>
          <IconButton size="small" onClick={() => setMobilePresetsOpen(false)} sx={{ color: 'text.secondary' }}>
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Box>
        {/* New Screen inside drawer */}
        <Button
          fullWidth size="small" variant="outlined"
          startIcon={<AddRoundedIcon />}
          onClick={() => { handleNewScreen(); setMobilePresetsOpen(false); }}
          sx={{
            textTransform: 'none', fontSize: '0.82rem', fontWeight: 500,
            borderRadius: '10px', mb: 2, justifyContent: 'flex-start',
            borderColor: 'rgba(0,0,0,0.12)', color: 'text.secondary',
            '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
          }}
        >
          New Screen
        </Button>
        <ScreenPresets {...presetProps} />
      </Drawer>

      {/* ── Premium Dialog ── */}
      <Dialog
        open={banner && !pageLoading}
        onClose={handleBannerClose}
        TransitionComponent={Transition}
        keepMounted
        aria-describedby="auth-dialog"
        maxWidth="md"
        sx={{
          '& .MuiDialog-paper': {
            maxHeight: 'calc(100% - 64px)', width: '100% !important',
            bgcolor: 'transparent', p: '0 !important', boxShadow: 0, textAlign: 'center',
          },
          '& .MuiDialogContent-root': { p: 0 },
          '& .MuiBackdrop-root': { backdropFilter: 'blur(3px)' },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <PremiumBanner maxWidth={12} {...pageProps} isPopup={true} user={user?.accessToken} message={message} screenerBanner={banner} logout={logoutUser} />
        </DialogContent>
      </Dialog>

      {/* ── Notifications ── */}
      <Snackbar TransitionProps={{ onExited: handleExited }} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={userUpdatedPreset === undefined} autoHideDuration={2000} onClose={() => setUserUpdatedPreset(null)}>
        <Alert onClose={() => setUserUpdatedPreset(null)} severity="error" sx={{ width: '100%' }}>
          Error updating your preset. Please try again later!
        </Alert>
      </Snackbar>
      <Snackbar TransitionProps={{ onExited: handleExited }} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={userUpdatedPreset} autoHideDuration={2000} onClose={() => setUserUpdatedPreset(null)}>
        <Alert onClose={() => setUserUpdatedPreset(null)} severity="success" sx={{ width: '100%' }}>
          {userUpdatedPreset} updated successfully!
        </Alert>
      </Snackbar>
      <Snackbar TransitionProps={{ onExited: handleExited }} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={userDeletedPreset} autoHideDuration={2000} onClose={() => setUserDeletedPreset(null)}>
        <Alert onClose={() => setUserDeletedPreset(null)} severity="success" sx={{ width: '100%' }}>
          Deleted successfully!
        </Alert>
      </Snackbar>

      <RenameScreener
        renamePreset={renamePreset}
        renameDialog={renameDialog}
        handleRenameClose={handleRenameClose}
        userUpdatedPreset={userUpdatedPreset}
        activePresetName={activePresetName}
        activePresetDesc={activePresetDesc}
        activePresetCode={activePresetCode}
        isPremium={isPremium}
        setBanner={setBanner}
      />
    </Box>
  );
}
